import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    Box,
    Typography
} from '@mui/material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import MDButton from 'components/MDButton';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

const ColumnSettingsPanel = ({ open, onClose, columns, columnOrder, onSave }) => {
    const [orderedFields, setOrderedFields] = useState(() => {
        // If columnOrder exists and has items, use it; otherwise use the original column order
        if (columnOrder && columnOrder.length > 0) {
            return columnOrder;
        }
        return columns.map(col => col.field);
    });

    // Only sync from props when the panel opens. Do NOT sync while panel is open so that
    // live WebSocket updates (which re-render parent and pass new column refs) don't
    // reset the reorder UI and disrupt drag-and-drop.
    const prevOpenRef = React.useRef(false);
    React.useEffect(() => {
        if (open && !prevOpenRef.current) {
            if (columnOrder && columnOrder.length > 0) {
                setOrderedFields(columnOrder);
            } else {
                setOrderedFields(columns.map(col => col.field));
            }
        }
        prevOpenRef.current = open;
    }, [open, columnOrder, columns]);

    const handleDragEnd = (result) => {
        if (!result.destination) return;

        const items = Array.from(orderedFields);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        setOrderedFields(items);
    };

    const handleSave = () => {
        onSave(orderedFields);
        onClose();
    };

    const handleReset = () => {
        const defaultOrder = columns.map(col => col.field);
        setOrderedFields(defaultOrder);
    };

    // Get column header name by field
    const getColumnName = (field) => {
        const column = columns.find(col => col.field === field);
        return column ? column.headerName || field : field;
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: '#1a2035',
                    color: '#fff'
                }
            }}
        >
            <DialogTitle>
                <Typography variant="h5" sx={{ color: '#fff' }}>
                    Reorder Columns
                </Typography>
                <Typography variant="body2" sx={{ color: '#9ca3af', mt: 1 }}>
                    Drag and drop to reorder columns
                </Typography>
            </DialogTitle>
            <DialogContent>
                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="columns">
                        {(provided, snapshot) => (
                            <List
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                                sx={{
                                    backgroundColor: snapshot.isDraggingOver ? '#2a3447' : 'transparent',
                                    borderRadius: 1,
                                    transition: 'background-color 0.2s ease'
                                }}
                            >
                                {orderedFields.map((field, index) => (
                                    <Draggable key={field} draggableId={field} index={index}>
                                        {(provided, snapshot) => (
                                            <ListItem
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                sx={{
                                                    backgroundColor: snapshot.isDragging ? '#344767' : '#202940',
                                                    mb: 1,
                                                    borderRadius: 1,
                                                    border: '1px solid #344767',
                                                    '&:hover': {
                                                        backgroundColor: '#2a3447'
                                                    },
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                <Box
                                                    {...provided.dragHandleProps}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        mr: 2,
                                                        cursor: 'grab',
                                                        '&:active': {
                                                            cursor: 'grabbing'
                                                        }
                                                    }}
                                                >
                                                    <DragIndicatorIcon sx={{ color: '#9ca3af' }} />
                                                </Box>
                                                <ListItemText
                                                    primary={getColumnName(field)}
                                                    secondary={`Field: ${field}`}
                                                    primaryTypographyProps={{
                                                        sx: { color: '#fff', fontWeight: 500 }
                                                    }}
                                                    secondaryTypographyProps={{
                                                        sx: { color: '#9ca3af', fontSize: '0.75rem' }
                                                    }}
                                                />
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: '#9ca3af',
                                                        backgroundColor: '#344767',
                                                        px: 1.5,
                                                        py: 0.5,
                                                        borderRadius: 1
                                                    }}
                                                >
                                                    {index + 1}
                                                </Typography>
                                            </ListItem>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </List>
                        )}
                    </Droppable>
                </DragDropContext>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <MDButton onClick={handleReset} variant="outlined" color="secondary" size="small">
                    Reset to Default
                </MDButton>
                <Box sx={{ flex: 1 }} />
                <MDButton onClick={onClose} variant="outlined" color="secondary" size="small">
                    Cancel
                </MDButton>
                <MDButton onClick={handleSave} variant="gradient" color="info" size="small">
                    Apply
                </MDButton>
            </DialogActions>
        </Dialog>
    );
};

export default ColumnSettingsPanel;
